import { Button } from "@/components/ui/Button";
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from "@/components/ui/Dialog";
import {TrashIcon, EyeIcon, ArrowDownTrayIcon} from "@heroicons/react/24/solid";
import React, { useState, useEffect } from "react";

interface UploadedSvgProps {
  filename: string;
  image: string | null;
  onRemove: () => void;
  isNewlyAdded?: boolean;
}


const SvgPreview = ({image}: {image: string | null}) => {
  if (!image) {
    return <p className="text-sm italic text-gray-500">(No preview available for EPS file)</p>;
  }
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={"secondary"}>
          <EyeIcon className={"h-4 w-4 mr-1"}/>
          Preview
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] bg-white">
        <DialogHeader>
          <DialogTitle>View SVG</DialogTitle>
        </DialogHeader>
        <div>
          <img src={image} alt="Uploaded SVG" className={"max-h-[75vh]"}/>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SvgItem = ({filename, image, onRemove, isNewlyAdded = false}: UploadedSvgProps) => {
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (isNewlyAdded) {
      let highlightTimeout: NodeJS.Timeout;
      
      // Start highlight after 0.5s delay
      const delayTimeout = setTimeout(() => {
        setIsHighlighted(true);
        
        // Remove highlight after 2s
        highlightTimeout = setTimeout(() => {
          setIsHighlighted(false);
        }, 2000);
      }, 500);
      
      return () => {
        clearTimeout(delayTimeout);
        if (highlightTimeout) clearTimeout(highlightTimeout);
      };
    }
  }, [isNewlyAdded]);

  const handleDownload = () => {
    if (!image) return;
    
    try {
      // Extract SVG content from DataURL
      let svgContent: string;
      
      if (image.startsWith('data:image/svg+xml;base64,')) {
        // Base64 encoded SVG
        const base64Content = image.split(',')[1];
        svgContent = atob(base64Content);
      } else if (image.startsWith('data:image/svg+xml;charset=utf-8,')) {
        // UTF-8 encoded SVG  
        svgContent = decodeURIComponent(image.split(',')[1]);
      } else if (image.startsWith('data:image/svg+xml,')) {
        // Plain SVG
        svgContent = decodeURIComponent(image.split(',')[1]);
      } else {
        // Fallback - assume it's already SVG content
        svgContent = image;
      }
      
      // Create blob and download
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      //console.error('Error downloading SVG:', error);
    }
  };

  return (
    <div className={"py-2 px-4 w-fit mt-2 flex flex-row gap-4 items-center"}>
      <span className={`px-2 py-1 rounded transition-all duration-500 ${
        isHighlighted ? 'bg-yellow-200' : 'bg-transparent'
      }`}>
        {filename}
      </span>
      <SvgPreview image={image}/>
      <Button onClick={handleDownload}
              variant={"ghost"}
              size={"icon"}
              className="text-blue-500 hover:bg-blue-200 hover:text-blue-500"
              title="Download SVG">
        <ArrowDownTrayIcon className={"h-4 w-4"}/>
      </Button>
      <Button onClick={onRemove}
              variant={"ghost"}
              size={"icon"}
              className="text-red-500 hover:bg-red-200 hover:text-red-500">
        <TrashIcon className={"h-4 w-4"}/>
      </Button>
    </div>
  )
}

export default SvgItem;